import { AudioDeviceInfo, VideoDeviceInfo } from "@azure/communication-calling";
import { Label, mergeStyles, Stack } from "@fluentui/react";
import React, { useEffect, useMemo } from "react";
import { dropDownStyles, mainStackTokens } from "../../styles/LocalDeviceSettings.styles";
import { useHandlers } from "../../hooks/useHandlers";
import { cameraAndVideoEffectsContainerStyleDesktop } from "../../styles/CallConfiguration.styles";
import { useTheme, VideoStreamOptions } from "@azure/communication-react";
import { useAdapter } from "../../provider/CallAdapterProvider";
import { DevicePermissionDropdown } from "./DeviceSelection/DevicePermissionsDropdown";
import { MicrophoneSelection } from "./DeviceSelection/MicrophoneSelection";
import { CameraSelection } from "./DeviceSelection/CameraSelection";
import { SpeakerSelection } from "./DeviceSelection/SpeakerSelection";
import { selectIsSafari, usePcpLiveLessonContext } from "../../store";
import { selectIsMobile } from "../../store/selectors/pcpSharedSelectors";
import { getDeviceManager } from "../../store/selectors/baseSelectors";


export interface AudioAndVideoDeviceSettingsProps {
    className?: string | undefined;

    cameras: VideoDeviceInfo[];
    microphones: AudioDeviceInfo[];
    speakers: AudioDeviceInfo[];
    selectedCamera?: VideoDeviceInfo;
    selectedMicrophone?: AudioDeviceInfo;
    selectedSpeaker?: AudioDeviceInfo;
    microphonePermissionGranted: boolean | undefined;
    cameraPermissionGranted: boolean | undefined;
    onSelectCamera: (device: VideoDeviceInfo, options?: VideoStreamOptions) => Promise<void>;
    onSelectMicrophone: (device: AudioDeviceInfo) => Promise<void>;
    onSelectSpeaker: (device: AudioDeviceInfo) => Promise<void>;
    onClickEnableDevicePermission?: () => void;
    renderTitle?: JSX.Element;
}


/**
 * Group auf Audio + Video + Microphone selection Dropdowns.
 */
export const AudioAndVideoDeviceSettings = (props: AudioAndVideoDeviceSettingsProps): JSX.Element => {
    const theme = useTheme();
    const adapter = useAdapter();
    const isMobile = usePcpLiveLessonContext(selectIsMobile);

    const defaultPlaceHolder = 'Option auswählen';
    const noSpeakersLabel = 'Keine Lautsprecher gefunden';

    const role = adapter.getState().call?.role;

    const cameraPermissionGranted = props.cameraPermissionGranted;
    const micPermissionGranted = props.microphonePermissionGranted;

    const roleCanUseCamera = role !== 'Consumer';
    const roleCanUseMic = role !== 'Consumer';

    const hasSpeakers = props.speakers.length > 0;
    const isSafariBrowser = usePcpLiveLessonContext(selectIsSafari);
    const isSafariWithNoSpeakers = isSafariBrowser && !hasSpeakers;

    const devices = usePcpLiveLessonContext(getDeviceManager);
    const isSpeakerSelectionAvailable = devices.isSpeakerSelectionAvailable;
    const isSpeakerSelectionUnavailable = !isSpeakerSelectionAvailable;

    // TODO: speaker permission is tied to microphone permission (when you request 'audio' permission using the SDK) its
    // actually granting access to query both microphone and speaker. However the browser popup asks you explicity for
    // 'microphone'. This needs investigation on how we want to handle this and maybe needs follow up with SDK team.
    /* @conditional-compile-remove(call-readiness) */
    useEffect(() => {
        if (cameraPermissionGranted) {
            adapter.queryCameras();
        }
        if (micPermissionGranted) {
            adapter.queryMicrophones();
        }

        if (isSpeakerSelectionAvailable) {
            adapter.querySpeakers();
        }
    }, [adapter, cameraPermissionGranted, micPermissionGranted, isSpeakerSelectionAvailable]);

    /* @conditional-compile-remove(call-readiness) */
    const dropdownProps = useHandlers(DevicePermissionDropdown);

    const getDefaultMicrophone = () => props.selectedMicrophone ?? defaultDevice(props.microphones);

    const defaultRenderTitle = useMemo(() => (
        <Stack horizontal horizontalAlign="space-between" styles={cameraAndVideoEffectsContainerStyleDesktop}>
            <Label
                id={'call-composite-local-camera-settings-label'}
                className={mergeStyles(dropDownStyles(theme).label)}
            >
                Audio und Video
            </Label>
        </Stack>
    ), [theme]);

    return (
        <Stack data-ui-id="call-composite-device-settings" tokens={mainStackTokens} className={mergeStyles(props.className)}>
            {props.renderTitle ?? defaultRenderTitle}
            {roleCanUseCamera && (
                <Stack>

                    <CameraSelection
                        isMobile={isMobile}
                        permissionGranted={cameraPermissionGranted ?? false}
                        selectedCamera={props.selectedCamera}
                        cameras={props.cameras}
                        onSelectCamera={props.onSelectCamera}
                        roleCanUseCamera={roleCanUseCamera}
                        dropdownProps={dropdownProps}
                        onClickEnableDevicePermission={props.onClickEnableDevicePermission}
                    />
                </Stack>
            )}
            <Stack>
                <Stack data-ui-id="call-composite-sound-settings" tokens={mainStackTokens}>
                    <MicrophoneSelection
                        isMobile={isMobile}
                        permissionGranted={micPermissionGranted === true}

                        defaultMicrophone={getDefaultMicrophone()}
                        microphones={props.microphones}
                        selectedMicrophone={props.selectedMicrophone}
                        onSelectMicrophone={props.onSelectMicrophone}

                        roleCanUseMic={roleCanUseMic}

                        onClickEnableDevicePermission={props.onClickEnableDevicePermission}
                        dropdownProps={dropdownProps}
                    />

                    {isSafariWithNoSpeakers || isSpeakerSelectionUnavailable ? <></> :
                        <SpeakerSelection
                            defaultPlaceHolder={defaultPlaceHolder}
                            onSelectSpeaker={props.onSelectSpeaker}
                            noSpeakersLabel={noSpeakersLabel}
                            speakers={props.speakers}
                            selectedSpeaker={props.selectedSpeaker}
                            defaultSpeaker={defaultDevice(props.speakers)}
                        />
                    }

                </Stack>
            </Stack>
        </Stack>
    );
};


const defaultDevice = (devices: AudioDeviceInfo[]) => {
    if (devices.length === 0) {
        return undefined;
    }
    const defaultDevice = devices.find((device) => device.isSystemDefault);
    if (defaultDevice) {
        return defaultDevice;
    }
    return devices[0];
};
